import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import AWS from 'aws-sdk';
import { FlagsProvider } from 'flagged';
import Guid from 'guid';
import { debounce } from 'lodash';
import { bindActionCreators } from 'redux';
import { Confirm, Dimmer, Loader } from 'semantic-ui-react';

import './App.css';

import { clearLocalStorageKeysByPrefix } from '@/common/utils/localStorage/clearLocalStorageKeysByPrefix';
import {
  PATIENT_WORK_QUEUE_FILTER_BUTTONS_STATE,
  PATIENT_WORK_QUEUE_SORTING_FILTERS_STATE,
} from '@/features/PatientWorkQueue/PatientWorkQueueTable.constants';
import { $fetchPatientWorkQueue } from '@/features/PatientWorkQueue/sagas/fetchPatientWorkQueue.saga';
import { SNACKBAR_TYPE } from '@/features/Snackbar/Snackbar.constants';
import { snackbarActions } from '@/features/Snackbar/Snackbar.slice';
import { XSOLIS_USER_TYPE } from '@/libs/Constants';

import featureFlagApi from './api/featureFlags';
import usersApi from './api/users.js';
import { DRAGONFLY_ADVISE_LOADING_MESSAGE } from './common/constants';
import CortexPaHeader from './components/CortexPaHeader/CortexPaHeader';
import MainHeader from './components/MainHeader/MainHeader';
import config from './config.js';
import Messages from './containers/Messages';
import { clearAllCustomPopPersistStates } from './containers/PopulationBuilderRR/PopulationColumnSettings';
import { clearAllWqPersistStates } from './containers/Queue/utils';
import SSOHeader from './containers/SsoHeader';
import { UserContext } from './contexts/UserContext.js';
import { initializeAnalytics } from './libs/analytics';
import { getCurrentUser, invokeApig } from './libs/awsLib';
import Cache from './libs/Cache';
import {
  FONT_SIZE_MULTIPLIER_SETTINGS,
  ROLES,
  STORAGE_KEYS,
} from './libs/Constants';
import IdleCheck from './libs/IdleCheck';
import { RealtimeClient } from './libs/RealtimeClient';
import RoleCheck from './libs/RoleCheck';
import Routes from './routes/routes';
import { userActions } from './store/user/User.slice';

export class App extends Component {
  constructor(props) {
    super(props);

    const initialUserState = {
      fontSizeMultiplier: 0,
      setFontSizeMultiplier: (fontSizeMultiplier, save = true) => {
        const MIN_SIZE = FONT_SIZE_MULTIPLIER_SETTINGS.MIN;
        const MAX_SIZE = FONT_SIZE_MULTIPLIER_SETTINGS.MAX;

        if (MIN_SIZE <= fontSizeMultiplier && fontSizeMultiplier <= MAX_SIZE) {
          this.setState((prev) => ({
            ...prev,
            user: {
              ...prev.user,
              fontSizeMultiplier: fontSizeMultiplier,
            },
          }));

          if (save) {
            const fontSizeMultiplierDebouncer = debounce((multiplier) => {
              if (this.state.user.fontSizeMultiplier === multiplier) {
                invokeApig({
                  body: { font_size_multiplier: multiplier },
                  method: 'PUT',
                  path: '/users/i/font-size-multiplier',
                  retry: true,
                }).catch((err) => console.error(err));
                // log error to datadog but silently handle it, this typically
                // happens due to cognito throttling
              }
            }, 1000);
            fontSizeMultiplierDebouncer(fontSizeMultiplier);
          }
        }
      },
      setUseAutoSave: (useAutoSave) =>
        this.setState((prev) => ({
          ...prev,
          user: {
            ...prev.user,
            useAutoSave: useAutoSave,
          },
        })),
      useAutoSave: true,
      username: null,
      userRoles: [],

      userToken: '',
      userTypeId: -1,
    };

    this.state = {
      alertMessage: '',
      alertOpen: false,
      banners: [],
      clientId: this.getClientId(),
      colors: [
        'orange',
        'yellow',
        'olive',
        'green',
        'teal',
        'violet',
        'purple',
        'pink',
        'brown',
        'grey',
        'red',
      ],
      facilitiesTimezones: [],
      featureFlags: [],
      iamworking: false,
      isAuthenticated: false,
      isAuthenticating: true,
      isDragonflyUser: false,
      isLoadingUserToken: true,
      myFacilities: [],
      myTeams: [],
      teamPending: [],
      totalPending: null,
      user: initialUserState,
      userGuideUrl: '',
      userProfile: {},
      users: [],
    };
    this.toggleWorking = this.toggleWorking.bind(this);
    this.turnOffWorker = this.turnOffWorker.bind(this);
    this.handleAlertCancel = this.handleAlertCancel.bind(this);
    this.handleAlertConfirm = this.handleAlertConfirm.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.handleProfile = this.handleProfile.bind(this);
    this.handleBannersAcknowledged = this.handleBannersAcknowledged.bind(this);
    this.cache = new Cache(this.state.user.userToken);

    if (window.DD_LOGS) {
      window.DD_LOGS.addLoggerGlobalContext('version', global.appVersion);
    }
  }

  getClientId = () => 'web-client:' + Guid.raw();

  handleAlertConfirm = () => {
    this.setState({ alertMessage: '', alertOpen: false });
    if (this.props.location.pathname === '/queue') {
      window.location.reload();
    }
  };

  handleBannersAcknowledged = () => {
    if (!this.state.banners || this.state.banners.length === 0) {
      return;
    }

    const ids = this.state.banners.map((banner) => banner.xpas_banner_id);
    invokeApig({
      body: ids,
      method: 'POST',
      path: '/banners/mine',
    });

    this.setState({ banners: [] });
  };

  handleAlertCancel = () =>
    this.setState({ alertMessage: '', alertOpen: false });

  toggleWorking = () => {
    if (!this.state.iamworking) {
      const work = this.iamWorking();
      work.then().then(this.updateWorkers);
      this.setState({ iamworking: true });
    } else {
      const work = this.iamNotWorking();
      work.then().then(this.updateWorkers);
      this.setState({ iamworking: false });
    }
  };

  turnOffWorker = (username) => {
    if (
      !RoleCheck(this.state.userRoles, [
        ROLES.SYSTEM_ADMINISTRATOR,
        ROLES.ADMINISTRATOR,
        ROLES.TEAM_LEAD,
        ROLES.SUPPORT,
      ])
    ) {
      return;
    }

    const newState = {
      workers: this.state.workers.filter((u) => u.username !== username),
    };
    if (username === this.state.user.username) {
      newState.iamworking = false;
    }

    this.setState(newState);

    const work = this.userNotWorking(username);
    work.then(() => {}).then(this.updateWorkers);
  };

  async componentDidUpdate(prevProps) {
    const routeChanged =
      prevProps.location.pathname !== this.props.location.pathname;
    if (this.state.user.username && routeChanged) {
      const bannersResults = await this.getMyBannerMessages();
      this.setState({ banners: bannersResults.banners });
    }
  }

  async componentDidMount() {
    this.setState({ logoutRedirectUrl: '' });
    const currentUser = getCurrentUser();

    if (currentUser === null) {
      this.setState({ isLoadingUserToken: false });
      return;
    }

    const newUserState = { ...this.state.user };

    try {
      const userToken = await this.getUserToken(currentUser);
      newUserState.userToken = userToken;

      const profile = await usersApi.getProfile(userToken);
      if (profile) {
        newUserState.userTypeId = profile.user_type_id;
        newUserState.useAutoSave = profile.use_auto_save;
        newUserState.username = profile.username;
        newUserState.userRoles = profile.user_roles;
        newUserState.isDragonflyUser = profile.isDragonflyUser;
        newUserState.fontSizeMultiplier = profile.font_size_multiplier ?? 0;
        newUserState.applicationUserId = profile.applicationUserId;
        newUserState.userTeams = profile.user_teams;

        this.props.userActions.SET_USER(profile);
      }
      this.updateUserToken(userToken);
    } catch (e) {
      console.error(e);
    }

    if (newUserState.userToken !== null) {
      let userStuff = await Promise.all([
        this.amIWorking(),
        this.cache.getUserGuide(),
        this.getMyBannerMessages(),
        featureFlagApi.getFeatureFlags(newUserState.userToken),
      ]);

      this.setState((prev) => ({
        ...prev,
        banners: userStuff[2].banners,
        featureFlags: userStuff[3].featureFlags,
        iamworking: userStuff[0].result,
        isDragonflyUser: newUserState.isDragonflyUser,
        userGuideUrl: userStuff[1],
        userRoles: newUserState.userRoles,
      }));

      this.updateWorkers();

      if (!config.DISABLE_IOT) {
        this.connectRealTimeClient(
          newUserState.userToken,
          newUserState.username
        );
      }

      this.updateTeamStats();
    }
    this.setState((prev) => ({
      ...prev,
      isLoadingUserToken: false,
      user: newUserState,
    }));
  }

  connectRealTimeClient = (userToken, username) => {
    if (this.alertClient) {
      return;
    }

    let clientId = this.state.clientId;

    let messageTopic = username + '/alert';
    this.alertClient = new RealtimeClient(clientId, userToken, [
      messageTopic,
      'review_completed',
    ]);

    this.alertClient.connect().then(() => {
      this.alertClient.onMessageReceived((topic, message) => {
        if (topic.toString().endsWith(messageTopic)) {
          if (message.message) {
            const { teamId } = this.props.match.params;
            const isXsolisUser =
              this.state.user.userTypeId === XSOLIS_USER_TYPE;
            this.props.snackbarActions.SET_SNACKBAR_DATA({
              closeCallback: () =>
                this.props.$fetchPatientWorkQueue({
                  isXsolisUser,
                  teamId,
                }),
              duration: 10000,
              message: message.message,
              type: SNACKBAR_TYPE.SUCCESS,
            });
          }
        } else if (topic.toString().endsWith('review_completed')) {
          this.updateTeamStats();
        }
      });
    });
  };

  iamWorking() {
    return invokeApig(
      { method: 'POST', path: `/users/iamworking`, service: 'USER' },
      this.state.user.userToken
    );
  }
  iamNotWorking() {
    return invokeApig(
      { method: 'PUT', path: `/users/iamnotworking`, service: 'USER' },
      this.state.user.userToken
    );
  }
  userNotWorking(username) {
    return invokeApig(
      {
        body: { username: username },
        method: 'PUT',
        path: `/users/iamnotworking`,
        service: 'USER',
      },
      this.state.user.userToken
    );
  }
  amIWorking() {
    return invokeApig(
      { method: 'GET', path: `/users/amiworking`, service: 'USER' },
      this.state.user.userToken
    );
  }
  getWorkers() {
    return invokeApig(
      { method: 'GET', path: `/users/working`, service: 'USER' },
      this.state.user.userToken
    );
  }
  getUserRoles() {
    return invokeApig(
      { method: 'GET', path: `/users/roles`, service: 'USER' },
      this.state.user.userToken
    );
  }
  getMyTeamStats() {
    return invokeApig(
      { method: 'GET', path: `/reporting/my-team-stats`, service: 'REPORTING' },
      this.state.user.userToken
    );
  }

  /**
   * @returns {Promise<{banners: []}>}
   */
  async getMyBannerMessages() {
    if (!this.state.user.userToken) {
      return { banners: [] };
    }

    try {
      return await invokeApig(
        { method: 'GET', path: `/banners/mine` },
        this.state.user.userToken
      );
    } catch (e) {
      console.error(e);
      return { banners: [] };
    }
  }

  getUserToken(currentUser) {
    return new Promise((resolve, reject) => {
      currentUser.getSession(function (err, session) {
        if (err) {
          reject(err);
          return;
        }
        resolve(session.getIdToken().getJwtToken());
      });
    });
  }

  getAttributes(currentUser) {
    return new Promise((resolve, reject) => {
      currentUser.getUserAttributes(function (err, result) {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }

  updateUserToken = (userToken) => {
    this.setState({
      userToken: userToken,
    });
  };

  updateWorkers = () => {
    const workers = this.getWorkers();
    workers.then((response) => {
      this.setState({ workers: response.users });
    });
  };

  updateTeamStats = () => {
    const myTeamStats = this.getMyTeamStats();
    myTeamStats.then((response) => {
      if (!response || response.length === 0) {
        return;
      }

      this.setState({ totalPending: response[0].pending });
      var found = response.filter((team) => {
        return team.xpas_team_id !== 0;
      });
      if (found.length > 0) {
        this.setState({ teamStats: found });
      }
    });
  };

  userHasAuthenticated = async (authenticated) => {
    this.setState({ isAuthenticated: authenticated, isLoadingUserToken: true });
    const currentUser = getCurrentUser();
    if (currentUser === null) {
      this.setState({ isLoadingUserToken: false });
      return;
    }
    const newUserState = { ...this.state.user };

    try {
      const userToken = await this.getUserToken(currentUser);
      const profile = await usersApi.getProfile(userToken);
      newUserState.userToken = userToken;
      if (profile) {
        newUserState.userTypeId = profile.user_type_id;
        newUserState.useAutoSave = profile.use_auto_save;
        newUserState.username = profile.username;
        newUserState.userRoles = profile.user_roles;
        newUserState.isDragonflyUser = profile.isDragonflyUser;
        newUserState.fontSizeMultiplier = profile.font_size_multiplier ?? 0;
        newUserState.applicationUserId = profile.applicationUserId;
        newUserState.userTeams = profile.user_teams;

        this.props.userActions.SET_USER(profile);
      }

      this.setState((prev) => ({
        ...prev,
        isDragonflyUser: profile.isDragonflyUser,
        user: { ...prev.user, ...newUserState },
        userRoles: profile.user_roles,
      }));
      this.updateUserToken(userToken);

      if (newUserState.username && typeof window.aptrinsic === 'function') {
        initializeAnalytics({
          applicationUserId: newUserState.applicationUserId,
          isDragonflyUser: newUserState.isDragonflyUser,
          primaryOrganizationId: profile.main_organization_id,
          primaryOrganizationName: profile.main_organization_name,
          roles: newUserState.userRoles?.map((r) => r.role_name),
          username: newUserState.username,
          userTypeId: newUserState.userTypeId,
        });
      }
    } catch (e) {
      console.error(e);
    }

    if (newUserState.userToken !== null) {
      let userStuff = await Promise.all([
        this.amIWorking(),
        this.cache.getUserGuide(),
        this.getMyBannerMessages(),
        featureFlagApi.getFeatureFlags(newUserState.userToken),
      ]);

      this.setState({
        banners: userStuff[2] ? userStuff[2].banners : [],
        featureFlags: userStuff[3].featureFlags,
        iamworking: userStuff[0].result,
        user: newUserState,
        userGuideUrl: userStuff[1],
      });

      this.updateWorkers();
      this.updateTeamStats();

      if (!config.DISABLE_IOT) {
        this.connectRealTimeClient(
          newUserState.userToken,
          newUserState.username
        );
      }
    }

    this.setState((prev) => ({ ...prev, isLoadingUserToken: false }));
  };

  handleProfile = () => {
    this.props.history.push('/profile');
  };

  handleLogoutCommon = async (options = {}) => {
    const newUserState = { user: {}, username: '', userToken: null };

    try {
      const currentUser = getCurrentUser();

      if (currentUser !== null) {
        currentUser.signOut();
      }

      if (AWS.config.credentials && AWS.config.credentials.clearCachedId) {
        await AWS.config.credentials.clearCachedId();
      }

      if (this.alertClient) {
        this.alertClient.close();
        this.alertClient = null;
      }

      const username = currentUser ? currentUser.getUsername() : null;
      this.cache.clear(username);

      window.sessionStorage.removeItem(STORAGE_KEYS.lastInvoiceParams);
      window.sessionStorage.removeItem(STORAGE_KEYS.recentlyViewColumns);

      clearAllCustomPopPersistStates();
      clearAllWqPersistStates();
      clearLocalStorageKeysByPrefix(PATIENT_WORK_QUEUE_FILTER_BUTTONS_STATE);
      clearLocalStorageKeysByPrefix(PATIENT_WORK_QUEUE_SORTING_FILTERS_STATE);

      this.props.userActions.RESET();
    } catch (e) {
      console.error(e);
    }

    this.setState((prev) => ({
      ...prev,
      isAuthenticated: false,
      ...options, // Dynamically merge optional state updates
      user: {
        ...prev.user,
        ...newUserState,
      },
    }));
  };

  handleLogout = () => {
    this.handleLogoutCommon();
  };

  handleLogoutIdle = () => {
    this.handleLogoutCommon({
      alertMessage: '',
      alertOpen: false,
    });
  };

  render() {
    const idleProps = {
      handleLogoutIdle: this.handleLogoutIdle,
      timeout: config.TIMEOUT,
      userToken: this.state.user.userToken,
    };
    const childProps = {
      facilitiesTimezones: this.state.facilitiesTimezones,
      getCurrentUser: getCurrentUser,
      isAuthenticated: this.state.isAuthenticated,
      isDragonflyUser: this.state.isDragonflyUser,
      myFacilities: this.state.myFacilities,
      turnOffWorker: this.turnOffWorker,
      updateTeamStats: this.updateTeamStats,
      updateUserToken: this.updateUserToken,
      userHasAuthenticated: this.userHasAuthenticated,
      userName: this.state.user.username,
      userProfile: this.state.userProfile,
      userRoles: this.state.user.userRoles,
      userToken: this.state.user.userToken,
      userTypeId: this.state.user.userTypeId,
      workers: this.state.workers,
    };

    if (this.state.isLoadingUserToken) {
      return (
        <Dimmer active inverted>
          <Loader>{DRAGONFLY_ADVISE_LOADING_MESSAGE}</Loader>
        </Dimmer>
      );
    }

    const isOnLoginPage = this.props.location.pathname === '/login';
    const isSsoPage = this.props.location.pathname === '/sso';

    return (
      !this.state.isLoadingUserToken && (
        <UserContext.Provider value={this.state.user}>
          <FlagsProvider features={this.state.featureFlags[0]}>
            <div className='App container'>
              <IdleCheck {...idleProps} />
              {isSsoPage && <SSOHeader />}
              {!isOnLoginPage &&
                !isSsoPage &&
                this.state.userToken &&
                this.state.isDragonflyUser && (
                  <MainHeader
                    colors={this.state.colors}
                    handleAutoAssignClick={this.toggleWorking}
                    handleLogoutClick={this.handleLogout}
                    handleProfileClick={this.handleProfile}
                    iamworking={this.state.iamworking}
                    teamStats={this.state.teamStats}
                    totalPending={this.state.totalPending}
                    userGuideUrl={this.state.userGuideUrl}
                    username={this.state.user.username}
                    userRoles={this.state.userRoles}
                    userToken={this.state.user.userToken}
                  />
                )}
              {!isOnLoginPage && !isSsoPage && !this.state.isDragonflyUser && (
                <CortexPaHeader
                  appVersion={global.appVersion}
                  colors={this.state.colors}
                  handleLogout={this.handleLogout}
                  handleProfile={this.handleProfile}
                  iamworking={this.state.iamworking}
                  teamStats={this.state.teamStats}
                  toggleWorking={this.toggleWorking}
                  totalPending={this.state.totalPending}
                  userGuideUrl={this.state.userGuideUrl}
                  username={this.state.user.username}
                  userRoles={this.state.userRoles}
                  userToken={this.state.user.userToken}
                />
              )}
              <div className={isOnLoginPage ? '' : 'ui fluid main container'}>
                <Messages
                  banners={this.state.banners}
                  handleBannersAcknowledged={this.handleBannersAcknowledged}
                />
                <Confirm
                  closeOnDimmerClick={true}
                  content={this.state.alertMessage}
                  onCancel={this.handleAlertCancel}
                  onConfirm={this.handleAlertConfirm}
                  open={this.state.alertOpen}
                />
                <Routes childProps={childProps} />
              </div>
            </div>
          </FlagsProvider>
        </UserContext.Provider>
      )
    );
  }
}

export default connect(null, (dispatch) => ({
  $fetchPatientWorkQueue: bindActionCreators($fetchPatientWorkQueue, dispatch),
  snackbarActions: bindActionCreators(snackbarActions, dispatch),
  userActions: bindActionCreators(userActions, dispatch),
}))(withRouter(App));
